import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Tooltip } from "@progress/kendo-react-tooltip";
import _ from "lodash";
import { icon, label, number } from "../../../../config";
import ModalPopUp from "../../../../shared/layouts/Modal/Modal";
import { searchEmailContacts } from "../../../../shared/services/user.service";
import EmailBodyEditor from "./EmailBodyEditor";
import EmailFlowCcRecipientsField from "./EmailFlowCcRecipientsField";
import "./EmailFlowConfigDialog.scss";
import {
  EMAIL_FLOW_ATTACHMENT_DEFAULT_FILE_NAME,
  EMAIL_FLOW_ATTACHMENT_INITIAL_VISIBLE_COUNT,
  EMAIL_FLOW_ATTACHMENT_OPTIONS,
  EMAIL_FLOW_ATTACHMENT_UI,
  TASK_PDF_TYPE,
  buildInitialAttachments,
  getDefaultAttachmentDraft,
  getKeysFromAllowedTypes,
  keysToAllowedTypes,
  normalizeAllowedTypes,
} from "./EmailFlowConstants";
import {
  FROM_OPTIONS,
  RECIPIENT_TYPES,
  SEARCH_DEBOUNCE_DELAY,
  SUBJECT_MAX_LENGTH,
  SUBJECT_PLACEHOLDERS,
  getToSystemOptionsMerged,
} from "./emailFlowConfig.constants";
import {
  buildContactRecipientPayload,
  buildManualEmailRecipient,
  buildPredefinedSystemRecipient,
  filterRecipientApiSuggestions,
  filterSystemRecipientOptions,
  handleRecipientFieldKeyDown,
  hasPredefinedRecipientWithLabel,
  isRecipientEmailSelected,
  removeRecipientById,
  validateManualRecipientEntry,
} from "./emailFlowRecipient.helper";
import { getInitials, getRecipientChipClass, getSystemRecipientOptionInitials, isValidEmail, parseReceivers, parseSender, parseSubject } from "./emailFlowConfig.utils";
import NodeDeleteConfirmPopup from "../../RuleBasedFlow/nodes/NodeDeleteConfirmPopup";

/**
 * Email Flow Configuration Dialog Component
 * Configures FROM, TO, CC, SUBJECT, and BODY fields for email workflow
 * Supports both new template creation and editing existing templates
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the dialog is open
 * @param {Function} props.onClose - Callback when dialog is closed
 * @param {Function} props.onSave - Callback when configuration is saved
 * @param {Object} props.data - Existing email template data (optional)
 * @param {boolean} [props.appendVariableRecipientOptions] - When true, TO/CC system list includes manager variable options
 * @author Sejal
 */
const EmailFlowConfigDialog = ({ isOpen, onClose, onSave, data, appendVariableRecipientOptions = false }) => {
  const toSystemRecipientOptions = useMemo(
    () => getToSystemOptionsMerged(appendVariableRecipientOptions),
    [appendVariableRecipientOptions]
  );
  /**
   * Compute initial values from existing data or use defaults
   */
  const initialValues = useMemo(() => {
    if (!data) {
      return {
        fromValue: FROM_OPTIONS[0],
        recipients: [],
        ccRecipients: [],
        subjectValue: "",
        bodyContent: "",
        attachments: [],
      };
    }
    // Convert parsed subject to string format
    // Replace value with placeholder for display in input
    const parsedSubject = parseSubject(data.subject);
    let subjectString = parsedSubject.segments.map((seg) => seg.value).join("") + parsedSubject.remainingText;

    // Find uniqueId placeholder to remove it from display
    const uniqueIdPlaceholder = SUBJECT_PLACEHOLDERS.find((p) => p.id === "ref-id");

    // Remove @uniqueId from display (it will be auto-appended when saving)
    if (uniqueIdPlaceholder) {
      subjectString = subjectString.replace(new RegExp(uniqueIdPlaceholder.value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"), "");
    }

    // Convert value to placeholder for display (only for refId, not uniqueId)
    const refIdPlaceholder = SUBJECT_PLACEHOLDERS.find((p) => p.id === "ticket-id");
    if (refIdPlaceholder) {
      // Replace " @refId " (with spaces) with placeholder
      subjectString = subjectString.replace(
        new RegExp(refIdPlaceholder.value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"),
        refIdPlaceholder.placeholder
      );
      // Also replace "@refId" (without spaces, in case it was typed directly)
      subjectString = subjectString.replace(/@refId\s*/g, refIdPlaceholder.placeholder);
      // Handle cases where @refId might be at the end or beginning
      subjectString = subjectString.replace(/\s*@refId\s*/g, refIdPlaceholder.placeholder);
    }

    // Clean up any extra spaces
    subjectString = subjectString.trim().replace(/\s+/g, " ");

    return {
      fromValue: parseSender(data.sender),
      recipients: parseReceivers(data.receiver),
      ccRecipients: parseReceivers(data.cc),
      subjectValue: subjectString,
      bodyContent: data.body || "",
      attachments: buildInitialAttachments(data),
    };
  }, [data]);
  const [previewText, setPreviewText] = useState("");
  const [fromValue, setFromValue] = useState(initialValues.fromValue);
  const [fromDropdownOpen, setFromDropdownOpen] = useState(false);
  const [recipients, setRecipients] = useState(initialValues.recipients);
  const [toInputValue, setToInputValue] = useState("");
  const [toDropdownOpen, setToDropdownOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [ccRecipients, setCcRecipients] = useState(initialValues.ccRecipients);
  const [showCc, setShowCc] = useState(initialValues.ccRecipients.length > number.ZERO);
  const [ccInputValue, setCcInputValue] = useState("");
  const [ccDropdownOpen, setCcDropdownOpen] = useState(false);
  const [ccSelectedIndex, setCcSelectedIndex] = useState(-1);
  const [ccApiSuggestions, setCcApiSuggestions] = useState([]);
  const [subjectValue, setSubjectValue] = useState(initialValues.subjectValue);
  const [validationError, setValidationError] = useState("");
  const [apiSuggestions, setApiSuggestions] = useState([]);
  const [bodyContent, setBodyContent] = useState(initialValues.bodyContent);
  const [attachments, setAttachments] = useState(initialValues.attachments);
  const [attachmentDraft, setAttachmentDraft] = useState(() => initialValues.attachments?.[0] ?? getDefaultAttachmentDraft());
  const [attachmentExpanded, setAttachmentExpanded] = useState(false);
  const [showAttachmentPopup, setShowAttachmentPopup] = useState(false);
  /** Index of attachment pending delete confirmation, or null */
  const [attachmentRemoveConfirmIndex, setAttachmentRemoveConfirmIndex] = useState(null);
  const attachmentSelectAllRef = useRef(null);
  const attachmentsRef = useRef(attachments);
  const attachmentRef = useRef(null);
  const attachmentDeleteTriggerRefs = useRef({});
  const attachmentDeleteRef = useRef(null);
  const attachmentBlurTimeoutRef = useRef(null);

  attachmentsRef.current = attachments;
  const fromDropdownRef = useRef(null);
  const toInputRef = useRef(null);
  const toContainerRef = useRef(null);
  const toDropdownRef = useRef(null);
  const ccInputRef = useRef(null);
  const ccContainerRef = useRef(null);
  const ccDropdownRef = useRef(null);
  const subjectInputRef = useRef(null);
  const subjectOverlayRef = useRef(null);

  /**
   * Debounced search function for API contacts
   */
  const debouncedSearch = useCallback(
    _.debounce(async (searchText) => {
      if (searchText.trim().length > 0) {
        const results = await searchEmailContacts(searchText);
        setApiSuggestions(results || []);
      } else {
        setApiSuggestions([]);
      }
    }, SEARCH_DEBOUNCE_DELAY),
    []
  );

  const debouncedCcSearch = useCallback(
    _.debounce(async (searchText) => {
      if (searchText.trim().length > 0) {
        const results = await searchEmailContacts(searchText);
        setCcApiSuggestions(results || []);
      } else {
        setCcApiSuggestions([]);
      }
    }, SEARCH_DEBOUNCE_DELAY),
    []
  );

  /**
   * Get filtered predefined options (excludes already selected)
   */
  const getFilteredPredefinedOptions = useCallback(() => {
    return filterSystemRecipientOptions(toSystemRecipientOptions, recipients, toInputValue);
  }, [toInputValue, recipients, toSystemRecipientOptions]);

  /**
   * Get filtered API suggestions (excludes already selected)
   */
  const getFilteredApiSuggestions = useCallback(() => {
    return filterRecipientApiSuggestions(apiSuggestions, recipients);
  }, [apiSuggestions, recipients]);

  const getFilteredCcPredefinedOptions = useCallback(() => {
    return filterSystemRecipientOptions(toSystemRecipientOptions, ccRecipients, ccInputValue);
  }, [ccInputValue, ccRecipients, toSystemRecipientOptions]);

  const getFilteredCcApiSuggestions = useCallback(() => {
    return filterRecipientApiSuggestions(ccApiSuggestions, ccRecipients);
  }, [ccApiSuggestions, ccRecipients]);

  /**
   * Handle click outside to close dropdowns
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fromDropdownRef.current && !fromDropdownRef.current.contains(event.target)) {
        setFromDropdownOpen(false);
      }
      if (toContainerRef.current && !toContainerRef.current.contains(event.target)) {
        setToDropdownOpen(false);
        setApiSuggestions([]);
      }
      if (ccContainerRef.current && !ccContainerRef.current.contains(event.target)) {
        setCcDropdownOpen(false);
        setCcApiSuggestions([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setAttachments(buildInitialAttachments(data));
  }, [data]);

  useEffect(() => {
    if (showAttachmentPopup) {
      const current = attachmentsRef.current?.[0];
      setAttachmentDraft(
        current
          ? {
              name: current.name?.trim() || EMAIL_FLOW_ATTACHMENT_DEFAULT_FILE_NAME,
              type: current.type || TASK_PDF_TYPE,
              includedFields: normalizeAllowedTypes(current.includedFields),
            }
          : getDefaultAttachmentDraft()
      );
      setAttachmentExpanded(false);
      setAttachmentRemoveConfirmIndex(null);
    }
  }, [showAttachmentPopup]);

  /**
   * Removes the attachment at the given index and closes the confirm UI.
   * @param {number} index - Attachment index to remove
   * @author Abhishek Dhillon
   */
  const confirmRemoveAttachmentAt = useCallback((index) => {
    setAttachments((prev) => (prev ?? []).filter((_, i) => i !== index));
    setAttachmentRemoveConfirmIndex(null);
  }, []);

  const hideAttachmentDeleteConfirm = useCallback(() => {
    setAttachmentRemoveConfirmIndex(null);
  }, []);

  const enabledAttachmentOptions = useMemo(() => EMAIL_FLOW_ATTACHMENT_OPTIONS.filter((o) => o.enabled), []);
  const attachmentDraftKeys = useMemo(() => getKeysFromAllowedTypes(attachmentDraft?.includedFields), [attachmentDraft?.includedFields]);
  const allEnabledAttachmentSelected =
    enabledAttachmentOptions.length > 0 && enabledAttachmentOptions.every((o) => attachmentDraftKeys.includes(o.key));
  const someEnabledAttachmentSelected = enabledAttachmentOptions.some((o) => attachmentDraftKeys.includes(o.key));

  useEffect(() => {
    if (attachmentSelectAllRef.current) {
      attachmentSelectAllRef.current.indeterminate = someEnabledAttachmentSelected && !allEnabledAttachmentSelected;
    }
  }, [someEnabledAttachmentSelected, allEnabledAttachmentSelected]);

  /**
   * Normalize subjectValue to convert @refId to ReferenceId placeholder
   * This handles cases where @refId might be in the value from loading existing data
   * Also removes @uniqueId from display (it's auto-appended in payload)
   */
  useEffect(() => {
    if (!subjectValue) return;

    let normalized = subjectValue;
    let hasChanges = false;

    // Remove @uniqueId from display (it's auto-appended in payload, should not be visible)
    const uniqueIdPlaceholder = SUBJECT_PLACEHOLDERS.find((p) => p.id === "ref-id");
    if (uniqueIdPlaceholder) {
      // Remove placeholder text
      const beforeUniqueId = normalized;
      normalized = normalized.replace(new RegExp(uniqueIdPlaceholder.placeholder.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"), "");
      // Remove @uniqueId if typed directly
      normalized = normalized.replace(/@uniqueId\s*/g, "");
      // Remove value format " @uniqueId "
      normalized = normalized.replace(new RegExp(uniqueIdPlaceholder.value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"), "");
      if (normalized !== beforeUniqueId) {
        hasChanges = true;
      }
    }

    // Convert @refId to ReferenceId placeholder
    if (normalized.includes("@refId")) {
      const refIdPlaceholder = SUBJECT_PLACEHOLDERS.find((p) => p.id === "ticket-id");
      if (refIdPlaceholder) {
        const beforeRefId = normalized;
        // Replace @refId (with or without spaces) with placeholder - use global replace
        normalized = normalized.replace(/\s*@refId\s*/g, refIdPlaceholder.placeholder);
        // Also handle the value format " @refId " (with spaces)
        normalized = normalized.replace(new RegExp(refIdPlaceholder.value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"), refIdPlaceholder.placeholder);
        if (normalized !== beforeRefId) {
          hasChanges = true;
        }
      }
    }

    // Only update if value actually changed to prevent infinite loops
    if (hasChanges && normalized !== subjectValue) {
      setSubjectValue(normalized);
    }
  }, [subjectValue]);

  /**
   * Generate preview text with example values for placeholders
   */
  useEffect(() => {
    let preview = subjectValue || "";

    // Find placeholder objects for matching
    const refIdPlaceholder = SUBJECT_PLACEHOLDERS.find((p) => p.id === "ticket-id");
    const uniqueIdPlaceholder = SUBJECT_PLACEHOLDERS.find((p) => p.id === "ref-id");

    // Remove any @uniqueId from preview (it shouldn't be displayed)
    if (uniqueIdPlaceholder) {
      preview = preview.replace(new RegExp(uniqueIdPlaceholder.placeholder.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"), "");
      preview = preview.replace(/@uniqueId\s*/g, "");
      preview = preview.replace(new RegExp(uniqueIdPlaceholder.value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"), "");
    }

    // Replace ReferenceId placeholder with example Reference ID (if present)
    if (refIdPlaceholder && preview.includes(refIdPlaceholder.placeholder)) {
      preview = preview.replace(new RegExp(refIdPlaceholder.placeholder.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"), " -TICKET-123- ");
    }

    // Clean up extra spaces
    preview = preview.trim().replace(/\s+/g, " ");

    // Always append Unique ID in preview (it's auto-appended in payload)
    const timestamp = new Date().getTime();
    preview = preview.trim() ? `${preview.trim()} #${timestamp}# (Unique ID is auto appended)` : `#${timestamp}# (Unique ID is auto appended)`;

    setPreviewText(preview);
  }, [subjectValue]);

  /**
   * Clear dropdown state after adding recipient
   */
  const clearDropdownState = () => {
    setToInputValue("");
    setApiSuggestions([]);
    setToDropdownOpen(false);
    setSelectedIndex(-1);
  };

  /**
   * Add recipient from predefined system options
   */
  const addPredefinedRecipient = (option) => {
    if (hasPredefinedRecipientWithLabel(recipients, option)) return;
    setRecipients((prev) => [...prev, buildPredefinedSystemRecipient(option)]);
    clearDropdownState();
    toInputRef.current?.blur();
  };

  /**
   * Add recipient from API search results
   */
  const addApiRecipient = (contact) => {
    if (isRecipientEmailSelected(recipients, contact.Email)) return;
    setRecipients((prev) => [...prev, buildContactRecipientPayload(contact)]);
    clearDropdownState();
  };

  /**
   * Add manually typed email recipient
   */
  const addManualRecipient = (email) => {
    const trimmedEmail = email.trim();
    const result = validateManualRecipientEntry(trimmedEmail, recipients);
    if (!result.ok) {
      if (result.error) setValidationError(result.error);
      clearDropdownState();
      return;
    }
    setRecipients((prev) => [...prev, buildManualEmailRecipient(result.lowerCaseEmail, `manual-${Date.now()}`)]);
    clearDropdownState();
  };

  /**
   * Remove recipient by Id
   */
  const removeRecipient = (recipientId) => {
    setRecipients(removeRecipientById(recipients, recipientId));
  };

  /** 
   * Clear CC dropdown state  
   */
  const clearCcDropdownState = () => {
    setCcInputValue("");
    setCcApiSuggestions([]);
    setCcDropdownOpen(false);
    setCcSelectedIndex(-1);
  };

  /**
   * Add predefined recipient to CC list
   */
  const addCcPredefinedRecipient = (option) => {
    if (hasPredefinedRecipientWithLabel(ccRecipients, option)) return;
    setCcRecipients((prev) => [...prev, buildPredefinedSystemRecipient(option)]);
    clearCcDropdownState();
    ccInputRef.current?.blur();
  };

  /** 
   * Add API recipient to CC list 
  */
  const addCcApiRecipient = (contact) => {
    if (isRecipientEmailSelected(ccRecipients, contact.Email)) return;
    setCcRecipients((prev) => [...prev, buildContactRecipientPayload(contact)]);
    clearCcDropdownState();
  };

  /**
   * Add manually typed email recipient to CC list
   */
  const addCcManualRecipient = (email) => {
    const trimmedEmail = email.trim();
    const result = validateManualRecipientEntry(trimmedEmail, ccRecipients);
    if (!result.ok) {
      if (result.error) setValidationError(result.error);
      clearCcDropdownState();
      return;
    }
    setCcRecipients((prev) => [...prev, buildManualEmailRecipient(result.lowerCaseEmail, `manual-cc-${Date.now()}`)]);
    clearCcDropdownState();
  };

  const removeCcRecipient = (recipientId) => {
    setCcRecipients(removeRecipientById(ccRecipients, recipientId));
  };

  /**
   * Render subject with pills for overlay display
   */
  const renderSubjectWithPills = () => {
    if (!subjectValue) return null;

    // Filter out uniqueId placeholder from display (only show refId)
    const displayPlaceholders = SUBJECT_PLACEHOLDERS.filter((p) => p.id !== "ref-id");
    const placeholderTexts = displayPlaceholders.map((p) => p.placeholder);
    const regexPattern = new RegExp(`(${placeholderTexts.map((v) => v.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "g");
    const parts = subjectValue.split(regexPattern);
    const elements = [];
    let currentPosition = 0;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (!part) continue;

      const placeholder = displayPlaceholders.find((p) => p.placeholder === part);
      if (placeholder) {
        // Calculate the position of this specific placeholder instance
        const placeholderPosition = currentPosition;
        // Render as pill - display label
        elements.push(
          <span key={`pill-${i}`} className='email-flow-subject-pill-overlay'>
            <span className='email-flow-subject-pill-text'>{placeholder.label}</span>
            <span
              className='email-flow-subject-pill-remove'
              onClick={(e) => {
                e.stopPropagation();
                removeSubjectPlaceholder(placeholder.placeholder, placeholderPosition);
              }}>
              {icon.CLOSE}
            </span>
          </span>
        );
        currentPosition += part.length;
      } else {
        // Render as text
        elements.push(<span key={`text-${i}`}>{part}</span>);
        currentPosition += part.length;
      }
    }
    return elements;
  };

  /**
   * Add placeholder pill to subject at cursor position
   */
  const addSubjectPlaceholder = (placeholder) => {
    const input = subjectInputRef.current;
    if (!input) return;

    const cursorPosition = input.selectionStart || subjectValue.length;
    const textBefore = subjectValue.substring(0, cursorPosition);
    const textAfter = subjectValue.substring(cursorPosition);
    // Use placeholder field for display in input
    const newValue = `${textBefore}${placeholder.placeholder}${textAfter}`;

    // Check character limit excluding Ref ID pills (pills don't count toward limit)
    // Since we're adding a pill, we only need to check if manually typed text is under limit
    if (getSubjectCharacterCount(newValue) <= SUBJECT_MAX_LENGTH) {
      setSubjectValue(newValue);

      // Restore cursor position after placeholder
      setTimeout(() => {
        const newCursorPos = cursorPosition + placeholder.placeholder.length;
        input.setSelectionRange(newCursorPos, newCursorPos);
        input.focus();
      }, 0);
    }
  };

  /**
   * Remove subject placeholder from string at specific position
   */
  const removeSubjectPlaceholder = (placeholderText, position) => {
    // Remove the specific occurrence at the given position
    const before = subjectValue.substring(0, position);
    const after = subjectValue.substring(position + placeholderText.length);
    const newValue = before + after;

    setSubjectValue(newValue);

    // Maintain cursor position
    setTimeout(() => {
      subjectInputRef.current?.focus();
    }, 0);
  };

  /**
   * Handle subject input change
   */
  const handleSubjectInputChange = (e) => {
    let value = e.target.value;

    // Remove uniqueId placeholder if user tries to type it (it's auto-appended)
    const uniqueIdPlaceholder = SUBJECT_PLACEHOLDERS.find((p) => p.id === "ref-id");
    if (uniqueIdPlaceholder) {
      value = value.replace(new RegExp(uniqueIdPlaceholder.placeholder.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"), "");
      // Also remove @uniqueId if typed directly
      value = value.replace(/@uniqueId\s*/g, "");
    }

    // Convert @refId (typed directly) to placeholder text for display
    const refIdPlaceholder = SUBJECT_PLACEHOLDERS.find((p) => p.id === "ticket-id");
    if (refIdPlaceholder) {
      // Match @refId with optional spaces around it
      value = value.replace(/@refId\s*/g, refIdPlaceholder.placeholder);
      // Also handle if user types the value with spaces " @refId "
      value = value.replace(/\s*@refId\s*/g, refIdPlaceholder.placeholder);
    }

    // Check character count excluding Ref ID pills
    if (getSubjectCharacterCount(value) <= SUBJECT_MAX_LENGTH) {
      setSubjectValue(value);
    }
  };

  /**
   * Handle subject input keydown
   */
  const handleSubjectKeyDown = (e) => {
    if (e.key === "Backspace" && subjectInputRef.current) {
      const input = subjectInputRef.current;
      const cursorPos = input.selectionStart;

      // Check each placeholder (use placeholder field for matching, exclude uniqueId)
      const displayPlaceholders = SUBJECT_PLACEHOLDERS.filter((p) => p.id !== "ref-id");
      for (const placeholder of displayPlaceholders) {
        const placeholderText = placeholder.placeholder;
        if (cursorPos >= placeholderText.length && subjectValue.slice(cursorPos - placeholderText.length, cursorPos) === placeholderText) {
          const before = subjectValue.slice(0, cursorPos - placeholderText.length);
          const after = subjectValue.slice(cursorPos);
          setSubjectValue(before + after);

          setTimeout(() => {
            input.setSelectionRange(cursorPos - placeholderText.length, cursorPos - placeholderText.length);
          }, 0);

          e.preventDefault();
          return;
        }
      }
    }
  };

  /**
   * Sync overlay scroll with input scroll
   */
  const syncSubjectScroll = () => {
    if (subjectInputRef.current && subjectOverlayRef.current) {
      subjectOverlayRef.current.scrollLeft = subjectInputRef.current.scrollLeft;
    }
  };

  /**
   * Calculate character count excluding Ref ID pills
   * Only counts manually typed alphabets by user
   * @param {string} value - Optional string value to count. If not provided, uses subjectValue
   */
  const getSubjectCharacterCount = (value = null) => {
    const textToCount = value !== null ? value : subjectValue;
    if (!textToCount) return 0;

    // Get Ref ID placeholder to exclude from count
    const refIdPlaceholder = SUBJECT_PLACEHOLDERS.find((p) => p.id === "ticket-id");
    if (!refIdPlaceholder) return textToCount.length;

    // Remove Ref ID placeholder from the string
    let countString = textToCount;
    countString = countString.replace(new RegExp(refIdPlaceholder.placeholder.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"), "");

    // Return the length of remaining characters (manually typed text)
    return countString.length;
  };

  /**
   * Get final subject string for saving
   * Converts placeholder text back to value for payload
   * Always appends @uniqueId at the end
   */
  const getSubjectString = () => {
    let result = subjectValue;

    // Find uniqueId placeholder
    const uniqueIdPlaceholder = SUBJECT_PLACEHOLDERS.find((p) => p.id === "ref-id");

    // Remove any existing @uniqueId from the string (we'll append it at the end)
    if (uniqueIdPlaceholder) {
      result = result.replace(new RegExp(uniqueIdPlaceholder.placeholder.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"), "");
      result = result.replace(new RegExp(uniqueIdPlaceholder.value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"), "");
    }

    // Replace placeholder text with value for payload (only for refId)
    SUBJECT_PLACEHOLDERS.forEach((placeholder) => {
      // Skip uniqueId as we handle it separately
      if (placeholder.id !== "ref-id") {
        result = result.replace(new RegExp(placeholder.placeholder.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"), placeholder.value);
      }
    });

    // Clean up extra spaces and trim
    result = result.trim().replace(/\s+/g, " ");

    // Always append @uniqueId at the end
    if (uniqueIdPlaceholder) {
      result = result ? `${result}${uniqueIdPlaceholder.value}` : uniqueIdPlaceholder.value.trim();
    }

    return result;
  };

  /**
   * Handle TO input key down
   */
  const handleToKeyDown = (e) => {
    handleRecipientFieldKeyDown(e, {
      filteredPredefined: getFilteredPredefinedOptions(),
      filteredApi: getFilteredApiSuggestions(),
      inputValue: toInputValue,
      recipients,
      selectedIndex,
      setSelectedIndex,
      onSelectPredefined: addPredefinedRecipient,
      onSelectApi: addApiRecipient,
      onAddManual: addManualRecipient,
      onRemoveLastRecipient: () => {
        const lastRecipient = recipients[recipients.length - 1];
        removeRecipient(lastRecipient.Id);
      },
      onEscape: () => {
        setToDropdownOpen(false);
        setSelectedIndex(-1);
        setApiSuggestions([]);
      },
    });
  };

  /**
   * Handle TO input change
   */
  const handleToInputChange = (e) => {
    const value = e.target.value;
    setToInputValue(value);
    setApiSuggestions([]);
    setSelectedIndex(-1);
    setToDropdownOpen(true);
    debouncedSearch(value);
  };

  /**
   * Handle TO input blur
   */
  const handleToBlur = () => {
    setTimeout(() => {
      if (toInputValue.trim()) {
        if (isValidEmail(toInputValue)) {
          addManualRecipient(toInputValue);
        } else {
          setToInputValue("");
        }
      }
    }, 200);
  };

  /**
   * Handle CC input key down
   */
  const handleCcKeyDown = (e) => {
    handleRecipientFieldKeyDown(e, {
      filteredPredefined: getFilteredCcPredefinedOptions(),
      filteredApi: getFilteredCcApiSuggestions(),
      inputValue: ccInputValue,
      recipients: ccRecipients,
      selectedIndex: ccSelectedIndex,
      setSelectedIndex: setCcSelectedIndex,
      onSelectPredefined: addCcPredefinedRecipient,
      onSelectApi: addCcApiRecipient,
      onAddManual: addCcManualRecipient,
      onRemoveLastRecipient: () => {
        const last = ccRecipients[ccRecipients.length - 1];
        removeCcRecipient(last.Id);
      },
      onEscape: () => {
        setCcDropdownOpen(false);
        setCcSelectedIndex(-1);
        setCcApiSuggestions([]);
      },
    });
  };

  /**
   * Handle CC input change
   */
  const handleCcInputChange = (e) => {
    const value = e.target.value;
    setCcInputValue(value);
    setCcApiSuggestions([]);
    setCcSelectedIndex(-1);
    setCcDropdownOpen(true);
    debouncedCcSearch(value);
  };

  const handleCcBlur = () => {
    setTimeout(() => {
      if (ccInputValue.trim()) {
        if (isValidEmail(ccInputValue)) {
          addCcManualRecipient(ccInputValue);
        } else {
          setCcInputValue("");
        }
      }
    }, 200);
  };

  /**
   * Handle save
   */
  const handleSave = () => {
    if (recipients.length === 0) {
      setValidationError("At least one recipient is required");
      return;
    }
    const config = {
      from: fromValue,
      to: recipients,
      cc: ccRecipients,
      subject: getSubjectString(),
      body: bodyContent,
      attachments,
    };

    if (onSave) {
      onSave(config);
    }
    onClose();
  };

  /**
   * Handle cancel - reset to initial values
   */
  const handleCancel = () => {
    setFromValue(initialValues.fromValue);
    setRecipients(initialValues.recipients);
    setCcRecipients(initialValues.ccRecipients);
    setShowCc(initialValues.ccRecipients.length > number.ZERO);
    setCcInputValue("");
    setCcDropdownOpen(false);
    setCcSelectedIndex(-1);
    setCcApiSuggestions([]);
    setSubjectValue(initialValues.subjectValue || "");
    setToInputValue("");
    setValidationError("");
    setApiSuggestions([]);
    setBodyContent(initialValues.bodyContent);
    setAttachments(initialValues.attachments);
    setAttachmentRemoveConfirmIndex(null);
    onClose();
  };

  /**
   * Close validation error
   */
  const closeValidationError = () => {
    setValidationError("");
  };

  /**
   * Toggle one attachment section in the draft (modal) state.
   * Parent component: EmailFlowConfigDialog
   * @param {string} key - Option key from EMAIL_FLOW_ATTACHMENT_OPTIONS
   * @author Abhishek Dhillon
   */
  const toggleAttachmentDraftOption = (key) => {
    const option = EMAIL_FLOW_ATTACHMENT_OPTIONS.find((o) => o.key === key);
    if (!option?.enabled) return;
    setAttachmentDraft((prev) => {
      const keys = new Set(getKeysFromAllowedTypes(prev.includedFields));
      if (keys.has(key)) keys.delete(key);
      else keys.add(key);
      return { ...prev, type: prev.type || TASK_PDF_TYPE, includedFields: keysToAllowedTypes([...keys]) };
    });
  };

  /**
   * Select or clear all enabled attachment options in the draft.
   * Parent component: EmailFlowConfigDialog
   * @author Abhishek Dhillon
   */
  const toggleAttachmentSelectAll = () => {
    const enabledKeys = EMAIL_FLOW_ATTACHMENT_OPTIONS.filter((o) => o.enabled).map((o) => o.key);
    const allOn = enabledKeys.length > 0 && enabledKeys.every((k) => attachmentDraftKeys.includes(k));
    setAttachmentDraft((prev) => {
      const prevKeys = getKeysFromAllowedTypes(prev.includedFields);
      if (allOn) {
        return { ...prev, includedFields: keysToAllowedTypes(prevKeys.filter((k) => !enabledKeys.includes(k))) };
      }
      return { ...prev, includedFields: keysToAllowedTypes([...new Set([...prevKeys, ...enabledKeys])]) };
    });
  };

  /**
   * Persist attachment modal selection and close the modal.
   * Parent component: EmailFlowConfigDialog
   * @author Abhishek Dhillon
   */
  const handleAttachmentModalSave = () => {
    setAttachments([
      {
        name: attachmentDraft.name?.trim() || EMAIL_FLOW_ATTACHMENT_DEFAULT_FILE_NAME,
        type: attachmentDraft.type || TASK_PDF_TYPE,
        includedFields: normalizeAllowedTypes(attachmentDraft.includedFields),
      },
    ]);
    setShowAttachmentPopup(false);
  };

  /**
   * Updates attachment file name in draft state.
   * Parent component: EmailFlowConfigDialog
   * @param {import('react').ChangeEvent<HTMLInputElement>} e
   * @author Abhishek Dhillon
   */
  const handleAttachmentDraftNameChange = (e) => {
    setAttachmentDraft((prev) => ({ ...prev, name: e.target.value }));
  };

  /**
   * Renders the Attachment modal body (checkbox grid, select all, more).
   * Parent component: EmailFlowConfigDialog
   * @returns {import('react').ReactNode}
   * @author Abhishek Dhillon
   */
  const renderAttachmentModalContent = () => {
    const visibleCount = attachmentExpanded ? EMAIL_FLOW_ATTACHMENT_OPTIONS.length : EMAIL_FLOW_ATTACHMENT_INITIAL_VISIBLE_COUNT;
    const visibleOptions = EMAIL_FLOW_ATTACHMENT_OPTIONS.slice(0, visibleCount);
    const showMoreLink = EMAIL_FLOW_ATTACHMENT_OPTIONS.length > EMAIL_FLOW_ATTACHMENT_INITIAL_VISIBLE_COUNT && !attachmentExpanded;

    return (
      <div className='email-flow-attachment-modal'>
        <div className='email-flow-attachment-modal__file-name'>
          <label className='email-flow-attachment-modal__file-name-label' htmlFor='email-flow-attachment-file-name'>
            {EMAIL_FLOW_ATTACHMENT_UI.FILE_NAME_LABEL}
          </label>
          <input
            id='email-flow-attachment-file-name'
            type='text'
            className='email-flow-attachment-modal__file-name-input'
            value={attachmentDraft.name ?? ""}
            onChange={handleAttachmentDraftNameChange}
            placeholder={EMAIL_FLOW_ATTACHMENT_DEFAULT_FILE_NAME}
          />
        </div>
        <div className='email-flow-attachment-modal__header-row'>
          <span className='email-flow-attachment-modal__sections-label'>{EMAIL_FLOW_ATTACHMENT_UI.SECTIONS_LABEL}</span>
          <label className='email-flow-attachment-modal__select-all'>
            <input
              ref={attachmentSelectAllRef}
              id='email-flow-attachment-select-all'
              type='checkbox'
              checked={allEnabledAttachmentSelected}
              onChange={toggleAttachmentSelectAll}
            />
            <span>{EMAIL_FLOW_ATTACHMENT_UI.SELECT_ALL}</span>
          </label>
        </div>
        <div className='email-flow-attachment-modal__grid' role='group' aria-label={EMAIL_FLOW_ATTACHMENT_UI.SECTIONS_LABEL}>
          {visibleOptions.map((opt) => (
            <label
              key={opt.key}
              className={`email-flow-attachment-modal__option ${!opt.enabled ? "is-disabled" : ""}`}
              htmlFor={`email-flow-attachment-option-${opt.key}`}>
              <input
                id={`email-flow-attachment-option-${opt.key}`}
                type='checkbox'
                checked={attachmentDraftKeys.includes(opt.key)}
                disabled={!opt.enabled}
                onChange={() => toggleAttachmentDraftOption(opt.key)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
        {showMoreLink && (
          <div className='email-flow-attachment-modal__more-row'>
            <button
              type='button'
              id='email-flow-attachment-more'
              className='email-flow-attachment-modal__more'
              onClick={() => setAttachmentExpanded(true)}>
              {EMAIL_FLOW_ATTACHMENT_UI.MORE}
            </button>
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  // Compute filtered values during render
  const filteredPredefinedOptions = getFilteredPredefinedOptions();
  const filteredApiSuggestions = getFilteredApiSuggestions();
  const hasDropdownOptions = filteredPredefinedOptions.length > 0 || filteredApiSuggestions.length > 0;
  const filteredCcPredefinedOptions = getFilteredCcPredefinedOptions();
  const filteredCcApiSuggestions = getFilteredCcApiSuggestions();
  const hasCcDropdownOptions = filteredCcPredefinedOptions.length > number.ZERO || filteredCcApiSuggestions.length > number.ZERO;

  return (
    <Dialog title='Configure Email Node' onClose={handleCancel} width={800} className='email-flow-config-dialog'>
      <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
        <div className='email-flow-dialog-content'>
          {/* FROM Field */}
          <div className='email-flow-field'>
            <label className='email-flow-label'>FROM</label>
            <div className='email-flow-from-dropdown' ref={fromDropdownRef}>
              <div className='email-flow-from-select' onClick={() => setFromDropdownOpen(!fromDropdownOpen)}>
                <span className='email-flow-from-value'>{fromValue.label}</span>
                <span className='email-flow-from-arrow'>{icon.CHEVRON_DOWN}</span>
              </div>
              {fromDropdownOpen && (
                <div className='email-flow-from-options'>
                  {FROM_OPTIONS.map((option) => (
                    <div
                      key={option.value}
                      className={`email-flow-from-option ${fromValue.value === option.value ? "selected" : ""}`}
                      onClick={() => {
                        setFromValue(option);
                        setFromDropdownOpen(false);
                      }}>
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* TO (+ optional CC) */}
          <div className='email-flow-field'>
            <div className='email-flow-to-header-row'>
              <label className='email-flow-label'>TO</label>
              {!showCc && (
                <span
                  className='email-flow-cc-link'
                  onClick={() => setShowCc(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setShowCc(true);
                    }
                  }}
                  role='button'
                  tabIndex={number.ZERO}>
                  Cc
                </span>
              )}
            </div>
            <div className='email-flow-to-container' ref={toContainerRef}>
              <div className='email-flow-to-input-wrapper' onClick={() => toInputRef.current?.focus()}>
                {recipients.map((recipient) => {
                  let displayName = recipient.Name || recipient.Email;
                  if (recipient.Type === RECIPIENT_TYPES.SYSTEM && displayName?.startsWith("@")) {
                    displayName = displayName.substring(1);
                  }
                  return (
                    <div key={recipient.Id} className={getRecipientChipClass(recipient.Type)}>
                      <span className='email-flow-chip-text'>{displayName}</span>
                      <span
                        className='email-flow-chip-remove'
                        onClick={(e) => {
                          e.stopPropagation();
                          removeRecipient(recipient.Id);
                        }}
                        title='Remove'>
                        {icon.CLOSE}
                      </span>
                    </div>
                  );
                })}
                <input
                  ref={toInputRef}
                  type='text'
                  className='email-flow-to-input'
                  value={toInputValue}
                  onChange={handleToInputChange}
                  onKeyDown={handleToKeyDown}
                  onBlur={handleToBlur}
                  onFocus={() => setToDropdownOpen(true)}
                  placeholder={recipients.length === 0 ? "Add recipients..." : ""}
                />
              </div>
              {toDropdownOpen && hasDropdownOptions && (
                <div className='email-flow-to-dropdown' ref={toDropdownRef}>
                  {filteredPredefinedOptions.length > 0 && (
                    <>
                      <div className='email-flow-dropdown-section-label'>System Recipients</div>
                      {filteredPredefinedOptions.map((option, index) => (
                        <div
                          key={option.id}
                          className={`email-flow-to-option ${selectedIndex === index ? "selected" : ""}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={(e) => {
                            e.stopPropagation();
                            addPredefinedRecipient(option);
                          }}
                          onMouseEnter={() => setSelectedIndex(index)}>
                          <div className='email-flow-to-option-avatar'>{getSystemRecipientOptionInitials(option)}</div>
                          <div className='email-flow-to-option-label'>{option.label}</div>
                        </div>
                      ))}
                    </>
                  )}
                  {filteredApiSuggestions.length > 0 && (
                    <>
                      <div className='email-flow-dropdown-section-label'>Contacts</div>
                      {filteredApiSuggestions.map((contact, index) => {
                        const adjustedIndex = filteredPredefinedOptions.length + index;
                        return (
                          <div
                            key={contact.Id || contact.Email}
                            className={`email-flow-to-option ${selectedIndex === adjustedIndex ? "selected" : ""}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={(e) => {
                              e.stopPropagation();
                              addApiRecipient(contact);
                            }}
                            onMouseEnter={() => setSelectedIndex(adjustedIndex)}>
                            {contact.PhotoLinks?.small ? (
                              <img src={contact.PhotoLinks.small} alt={contact.Name} className='email-flow-to-option-photo' />
                            ) : (
                              <div className='email-flow-to-option-avatar'>{getInitials(contact.Name || contact.Email)}</div>
                            )}
                            <div className='email-flow-to-option-details'>
                              <div className='email-flow-to-option-name'>{contact.Name || contact.Email}</div>
                              {contact.Name && contact.Email && <div className='email-flow-to-option-email'>{contact.Email}</div>}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              )}
            </div>
            {showCc && (
              <div className='email-flow-cc-section'>
                <EmailFlowCcRecipientsField
                  recipients={ccRecipients}
                  inputValue={ccInputValue}
                  inputRef={ccInputRef}
                  containerRef={ccContainerRef}
                  dropdownRef={ccDropdownRef}
                  dropdownOpen={ccDropdownOpen}
                  hasDropdownOptions={hasCcDropdownOptions}
                  filteredPredefinedOptions={filteredCcPredefinedOptions}
                  filteredApiSuggestions={filteredCcApiSuggestions}
                  selectedIndex={ccSelectedIndex}
                  onInputChange={handleCcInputChange}
                  onKeyDown={handleCcKeyDown}
                  onBlur={handleCcBlur}
                  onFocus={() => setCcDropdownOpen(true)}
                  onRemoveRecipient={removeCcRecipient}
                  onSelectPredefined={addCcPredefinedRecipient}
                  onSelectApi={addCcApiRecipient}
                  onOptionMouseEnter={setCcSelectedIndex}
                />
              </div>
            )}
          </div>

          {/* SUBJECT Field */}
          <div className='email-flow-field'>
            <div className='email-flow-subject-header'>
              <label className='email-flow-label'>SUBJECT</label>
              <div className='email-flow-subject-actions'>
                {SUBJECT_PLACEHOLDERS.filter((p) => p.id !== "ref-id").map((placeholder) => (
                  <span key={placeholder.id} className='email-flow-subject-add-link' onClick={() => addSubjectPlaceholder(placeholder)}>
                    Add {placeholder.label}
                  </span>
                ))}
              </div>
            </div>

            <div className='email-flow-subject-container'>
              {/* Overlay for visual pill rendering */}
              <div className='email-flow-subject-overlay' ref={subjectOverlayRef}>
                <span className='email-flow-subject-overlay-content'>{renderSubjectWithPills()}</span>
              </div>

              {/* Actual input (transparent text, visible cursor) */}
              <input
                ref={subjectInputRef}
                type='text'
                className='email-flow-subject-input-overlay'
                value={subjectValue}
                onChange={handleSubjectInputChange}
                onScroll={syncSubjectScroll}
                onInput={syncSubjectScroll}
                onKeyUp={syncSubjectScroll}
                onClick={syncSubjectScroll}
                onKeyDown={handleSubjectKeyDown}
                spellCheck={false}
                placeholder={subjectValue.length === 0 ? "Enter email subject..." : ""}
              />
            </div>

            <div className='email-flow-subject-counter'>
              {getSubjectCharacterCount()}/{SUBJECT_MAX_LENGTH}
            </div>
            <div className='email-flow-subject-preview d-flex align-items-center justify-content-between'>
              <div>
                <label className='email-flow-preview-label'>Preview:</label>
                <span className='email-flow-preview-text'>{previewText}</span>
              </div>
              <div className='d-flex align-items-center gap-2'>
                {(attachments?.length ?? 0) > 0 &&
                  attachments?.map((attachment, index) => (
                    <span
                      key={`${attachment.name}-${index}`}
                      className='email-flow-attachment-summary position-relative'
                      title={attachment.name}
                      data-attachment-row={index}>
                      <div className='d-flex align-items-center justify-content-between gap-1'>
                        <div>{attachment.name}</div>
                        <div className='cursor-pointer' title='Edit'>
                          <span
                            id={`email-flow-attachment-edit-${index}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setAttachmentRemoveConfirmIndex(null);
                              setShowAttachmentPopup(true);
                            }}>
                            {icon.EDIT_ICON}
                          </span>
                        </div>
                        <div className='cursor-pointer' title='Remove'>
                          <span
                            id={`email-flow-attachment-remove-trigger-${index}`}
                            ref={(element) => {
                              if (element) {
                                attachmentDeleteTriggerRefs.current[index] = element;
                              } else {
                                delete attachmentDeleteTriggerRefs.current[index];
                              }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setAttachmentRemoveConfirmIndex(index);
                            }}
                            title='Remove'>
                            {icon.DELETE_ICON}
                          </span>
                        </div>
                      </div>
                    </span>
                  ))}
                {attachmentRemoveConfirmIndex !== null && attachmentDeleteTriggerRefs.current[attachmentRemoveConfirmIndex] && (
                  <NodeDeleteConfirmPopup
                    anchor={attachmentDeleteTriggerRefs.current[attachmentRemoveConfirmIndex]}
                    deleteRef={attachmentDeleteRef}
                    blurTimeoutRef={attachmentBlurTimeoutRef}
                    onConfirm={() => confirmRemoveAttachmentAt(attachmentRemoveConfirmIndex)}
                    onCancel={hideAttachmentDeleteConfirm}
                    hideOnBlur={hideAttachmentDeleteConfirm}
                  />
                )}
                <Button
                  id='email-flow-open-attachment-btn'
                  className='btn btn-primary'
                  size='small'
                  ref={attachmentRef}
                  disabled={(attachments?.length ?? 0) >= 1}
                  onClick={() => setShowAttachmentPopup(true)}>
                  Attachment
                </Button>
                {showAttachmentPopup && (
                  <ModalPopUp
                    content={renderAttachmentModalContent}
                    heading='Attachment'
                    className='export-modal-popup email-flow-attachment-popup'
                    id='email-flow-attachment-popup'
                    buttons={[
                      {
                        label: EMAIL_FLOW_ATTACHMENT_UI.CANCEL,
                        action: "hide",
                      },
                      {
                        label: EMAIL_FLOW_ATTACHMENT_UI.SAVE,
                        action: "confirm",
                      },
                    ]}
                    confirm={handleAttachmentModalSave}
                    hide={() => setShowAttachmentPopup(false)}
                    show={showAttachmentPopup}
                    width='450px'
                  />
                )}
              </div>
            </div>
          </div>

          {/* BODY Field */}
          <div className='email-flow-field'>
            <label className='email-flow-label'>BODY</label>
            <EmailBodyEditor value={bodyContent} onChange={setBodyContent} placeholder='Compose your email body here...' />
          </div>
        </div>

        {/* Footer Buttons */}
        <div className='email-flow-dialog-footer'>
          <button className='btn btn-ghost' onClick={handleCancel}>
            Cancel
          </button>
          <button className='btn btn-primary' onClick={handleSave}>
            Save Configuration
          </button>
        </div>

        {/* Validation Error Modal */}
        {validationError && (
          <div className='email-flow-error-overlay' onClick={closeValidationError}>
            <div className='email-flow-error-modal' onClick={(e) => e.stopPropagation()}>
              <div className='email-flow-error-header'>
                <h3 className='email-flow-error-title'>Validation Error</h3>
              </div>
              <div className='email-flow-error-body'>
                <p className='email-flow-error-message'>{validationError}</p>
              </div>
              <div className='email-flow-error-footer'>
                <button className='email-flow-error-button' onClick={closeValidationError}>
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
      </Tooltip>
    </Dialog>
  );
};

export default EmailFlowConfigDialog;
